'use client';

import { useRestaurantSite } from '@/src/lib/hospitality/useRestaurantSite';
import type { DayOfWeek } from '@/src/lib/hospitality/wire';
import {
  BlockSection,
  EmptyNote,
  SiteStateNotice,
  clockTime,
  localDateLabel,
  useActiveLocale,
  useHospitalityMessages,
} from './shared';

/**
 * When the restaurant is open, and the days it is not.
 *
 * <p>Both lists come from `RestaurantSiteDto` — `hours` and `closures` — and
 * neither is computed here. A closure's `reason` is rendered exactly as the
 * operator wrote it: `Yılbaşı` is `Yılbaşı`, never transliterated and never
 * machine-translated.
 *
 * <p>Times are printed as the backend wrote them. They are `LocalTime`s in the
 * channel's own zone, so there is nothing to convert and converting would be
 * the bug — a UK server must not render an Istanbul restaurant's 12:00 as
 * 09:00.
 */
export interface OpeningHoursProps {
  heading?: string;
  /** Show the closed-dates list. Off by default: most sites want the weekly
   *  table on the page and the exceptions only where they matter. */
  showClosures?: boolean;
}

/** Monday first. A fixed order, not a locale-dependent one — the backend sends
 *  whatever order it likes, and a table that reorders itself between two loads
 *  is unreadable. */
const WEEK: DayOfWeek[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

export function OpeningHours({ heading, showClosures = false }: OpeningHoursProps) {
  const t = useHospitalityMessages();
  const locale = useActiveLocale();
  const state = useRestaurantSite();

  if (state.status !== 'ready') {
    return (
      <BlockSection heading={heading ?? t('hospitality.hours.heading')}>
        <SiteStateNotice state={state} t={t} />
      </BlockSection>
    );
  }

  const { site } = state;
  const byDay = new Map<DayOfWeek, { opensAt: string | null; closesAt: string | null }[]>();
  for (const row of site.hours) {
    const rows = byDay.get(row.dayOfWeek) ?? [];
    rows.push({ opensAt: row.opensAt, closesAt: row.closesAt });
    byDay.set(row.dayOfWeek, rows);
  }

  return (
    <BlockSection heading={heading ?? t('hospitality.hours.heading')}>
      {site.hours.length === 0 ? (
        <EmptyNote>{t('hospitality.hours.empty')}</EmptyNote>
      ) : (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
          {WEEK.map((day) => {
            const rows = (byDay.get(day) ?? []).filter((row) => row.opensAt && row.closesAt);
            return (
              <div key={day} className="contents">
                <dt className="font-medium">{t(`hospitality.day.${day}`)}</dt>
                <dd className="text-[var(--color-text-secondary)]">
                  {rows.length === 0
                    ? t('hospitality.hours.closed')
                    : rows
                        .map((row) => `${clockTime(row.opensAt)}–${clockTime(row.closesAt)}`)
                        .join(', ')}
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      {showClosures && site.closures.length > 0 ? (
        <div className="mt-6">
          <h3 className="text-base font-medium">{t('hospitality.closures.heading')}</h3>
          <ul className="mt-2 space-y-1 text-sm text-[var(--color-text-secondary)]">
            {site.closures.map((closure) => (
              <li key={`${closure.closedOn}-${closure.reason ?? ''}`}>
                {localDateLabel(closure.closedOn, locale)}
                {closure.reason ? ` — ${closure.reason}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </BlockSection>
  );
}
